import { useQuery } from "@tanstack/react-query";
import { getApiBase, withPartNumberSkuQuery } from "../helpers/helpers";
import { withIDBFallback, IDB_STORES, IDB_TTL } from "./IDBCacheService";

export type VehicleAddonItem = {
    sku: string;
    variantId: number;
    productName: string;
    price: string;
    compareAtPrice: string | null;
    image: string | null;
};

const fetchVehicleAddons = async (params: {
    year: string | number;
    make: string;
    model: string;
    trim?: string;
    cab?: string;
}): Promise<VehicleAddonItem[]> => {
    const idbKey = `vehicle_addons_${params.year}_${params.make}_${params.model}_${params.trim ?? ""}_${params.cab ?? ""}`;
    return withIDBFallback(
        IDB_STORES.VEHICLE_ADDONS,
        idbKey,
        IDB_TTL.VEHICLE_ADDONS,
        async () => {
            const url = new URL(`https://scsbiz3.wearevisum.com/api/v1/vehicle-addons`);
            url.searchParams.set("year", String(params.year));
            url.searchParams.set("make", params.make);
            url.searchParams.set("model", params.model);
            if (params.trim) url.searchParams.set("trim", params.trim);
            if (params.cab) url.searchParams.set("cab", params.cab);

            const response = await fetch(withPartNumberSkuQuery(url.toString()), {
                mode: "cors",
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch vehicle addons: ${response.statusText}`);
            }

            const data = await response.json();
            return data.data ?? [];
        }
    );
};

export const useVehicleAddonsQuery = (params: {
    year?: string | number;
    make?: string;
    model?: string;
    trim?: string;
    cab?: string;
}) => {
    const enabled = Boolean(params.year && params.make && params.model);
    return useQuery({
        queryKey: [
            "vehicle-addons",
            params.year,
            params.make,
            params.model,
            params.trim ?? "",
            params.cab ?? "",
        ],
        queryFn: () =>
            fetchVehicleAddons({
                year: params.year!,
                make: params.make!,
                model: params.model!,
                trim: params.trim,
                cab: params.cab,
            }),
        enabled,
        staleTime: 1000 * 60 * 15,
        gcTime: 1000 * 60 * 30,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: false,
    });
};
